import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import {
  AdminMetric,
  AdminSupportingText,
  AdminMainText,
  AdminMetadataText,
  AdminFormSecondaryAction,
} from "../../sections/AdminDashboard/shared/adminUx";
import { AdminWidgetHeader, AdminWidgetPeriodFilter } from "../BusinessDashboard/AdminWidgetChrome";
import * as echarts from "echarts";

const ReviewsWidget = () => {
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const [filterPeriod, setFilterPeriod] = useState("Last 7 Days");

  const reviewsDataByPeriod = {
    "Last 7 Days": {
      total: 24,
      chartData: [1, 2, 3, 5, 6, 7, 8],
      reviews: [
        {
          id: 1,
          businessName: "Liberty Plumbing",
          rating: 5,
          reviewText: "Excellent service, punctual and polite…",
          timestamp: "2h ago",
        },
        {
          id: 2,
          businessName: "Apex Electrical",
          rating: 4,
          reviewText: "Job done well but could improve communication…",
          timestamp: "Yesterday",
        },
        {
          id: 3,
          businessName: "Sparkle Cleaners",
          rating: 5,
          reviewText: "Super quick and left the place spotless!",
          timestamp: "4d ago",
        },
      ],
    },
    "Last 14 Days": {
      total: 38,
      chartData: [2, 3, 1, 4, 5, 3, 6, 4, 2, 5, 3, 4, 6, 7],
      reviews: [
        {
          id: 1,
          businessName: "Liberty Plumbing",
          rating: 5,
          reviewText: "Excellent service, punctual and polite…",
          timestamp: "2h ago",
        },
        {
          id: 4,
          businessName: "Pro Painters",
          rating: 5,
          reviewText: "Amazing work on our house exterior. Highly recommend!",
          timestamp: "6d ago",
        },
        {
          id: 5,
          businessName: "Garden Masters",
          rating: 4,
          reviewText: "Good service but a bit pricey.",
          timestamp: "10d ago",
        },
      ],
    },
    "Last 30 Days": {
      total: 67,
      chartData: [3, 4, 2, 5, 6, 3, 7, 5, 3, 6, 4, 5, 7, 6, 4, 5, 3, 6, 7, 5, 4, 6, 5, 7, 6, 5, 7, 8, 6, 9],
      reviews: [
        {
          id: 1,
          businessName: "Liberty Plumbing",
          rating: 5,
          reviewText: "Excellent service, punctual and polite…",
          timestamp: "2h ago",
        },
        {
          id: 6,
          businessName: "Roof Experts",
          rating: 5,
          reviewText: "Professional roof inspection and repair service.",
          timestamp: "18d ago",
        },
        {
          id: 7,
          businessName: "Clean Pro Services",
          rating: 4,
          reviewText: "Thorough cleaning, will use again.",
          timestamp: "25d ago",
        },
      ],
    },
    "Last 90 Days": {
      total: 142,
      chartData: [4, 5, 3, 6, 7, 5, 8, 6, 4, 7, 5, 6, 8, 7, 5, 6, 4, 7, 8, 6, 5, 7, 6, 8, 7, 6, 8, 9, 7, 10],
      reviews: [
        {
          id: 1,
          businessName: "Liberty Plumbing",
          rating: 5,
          reviewText: "Excellent service, punctual and polite…",
          timestamp: "2h ago",
        },
        {
          id: 8,
          businessName: "Elite HVAC",
          rating: 5,
          reviewText: "Fast and efficient AC repair service!",
          timestamp: "48d ago",
        },
        {
          id: 9,
          businessName: "Locksmith 24/7",
          rating: 4,
          reviewText: "Quick response time, professional work.",
          timestamp: "72d ago",
        },
      ],
    },
  };

  const reviewsData = reviewsDataByPeriod[filterPeriod];
  const recentReviews = reviewsData.reviews;

  useEffect(() => {
    if (!chartRef.current) return;
    const chart = echarts.init(chartRef.current);

    const getXAxisLabels = () => {
      switch (filterPeriod) {
        case "Last 7 Days":
          return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
        case "Last 14 Days":
          return Array.from({ length: 14 }, (_, i) => `Day ${i + 1}`);
        case "Last 30 Days":
          return Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`);
        case "Last 90 Days":
          return Array.from({ length: 90 }, (_, i) => `Day ${i + 1}`);
        default:
          return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      }
    };

    const option = {
      tooltip: { show: false },
      grid: { left: 0, right: 0, top: 5, bottom: 0, containLabel: false },
      xAxis: { type: "category", show: false, data: getXAxisLabels() },
      yAxis: { type: "value", show: false },
      series: [
        {
          data: reviewsData.chartData,
          type: "bar",
          barWidth: "60%",
          itemStyle: { color: "#2e00ff", borderRadius: [3, 3, 0, 0] },
        },
      ],
    };
    chart.setOption(option);
    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [filterPeriod, reviewsData.chartData]);

  const handleViewAll = () => {
    navigate("/customer-dashboard/inbox/reviews");
  };

  const renderStars = (rating) =>
    [1, 2, 3, 4, 5].map((star) => (
      <span key={star} className={star <= rating ? "text-warmyellow" : "text-lightgray"}>
        ★
      </span>
    ));

  return (
    <DashboardMainContainer className="masonry-item w-[400px] h-[450px] flex flex-col overflow-hidden">
      <AdminWidgetHeader title="Reviews">
        <AdminWidgetPeriodFilter
          period={filterPeriod}
          onPeriodChange={setFilterPeriod}
          periods={["Last 7 Days", "Last 14 Days", "Last 30 Days", "Last 90 Days"]}
        />
      </AdminWidgetHeader>

      <div className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <AdminMetric size="large" className="text-offblack mb-1 block">
              {reviewsData.total}
            </AdminMetric>
            <AdminSupportingText className="text-offblack block">
              Total Reviews Given
            </AdminSupportingText>
          </div>
          <div className="w-36 h-16 flex-shrink-0">
            <div ref={chartRef} className="w-full h-full" />
          </div>
        </div>

        <div className="space-y-2 border-t border-lightgray pt-3">
          {recentReviews.length > 0 ? (
            recentReviews.map((review) => (
              <div
                key={review.id}
                onClick={() => navigate("/customer-dashboard/inbox/reviews")}
                className="cursor-pointer transition-colors p-2 hover:bg-mistygray rounded-lg"
              >
                <div className="flex items-start justify-between mb-1">
                  <AdminMainText className="font-medium text-offblack">
                    {review.businessName}
                  </AdminMainText>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span className="flex items-center gap-0.5">{renderStars(review.rating)}</span>
                    <AdminMetadataText>{review.timestamp}</AdminMetadataText>
                  </div>
                </div>
                <AdminMainText className="text-mediumgray line-clamp-2 block">
                  {review.reviewText}
                </AdminMainText>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <AdminSupportingText className="text-offblack mb-2 block">
                No reviews yet.
              </AdminSupportingText>
              <AdminMetadataText className="text-mediumgray block">
                Leave a review when you hire a trade.
              </AdminMetadataText>
            </div>
          )}
        </div>

        <div className="pt-2">
          <AdminFormSecondaryAction type="button" onClick={handleViewAll} className="!w-full">
            View all reviews
          </AdminFormSecondaryAction>
        </div>
      </div>
    </DashboardMainContainer>
  );
};

export default ReviewsWidget;
